import { useEffect, useState } from 'react';
import { api, type AdminUser, type GlobalRole } from '../api';
import { useAuth } from '../auth';
import { Page, Card, Button, Field, Input, Select, Badge, Modal, EmptyState } from '../ui';

export function Users() {
  const { me } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // form de criação
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [globalRole, setGlobalRole] = useState<GlobalRole>('user');
  const [busy, setBusy] = useState(false);

  function reload() {
    setLoading(true);
    api
      .listUsers()
      .then(({ users }) => setUsers(users))
      .catch(() => setErr('Falha ao carregar usuários'))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (me?.globalRole === 'admin') reload();
    else setLoading(false);
  }, [me?.globalRole]);

  if (me?.globalRole !== 'admin') {
    return (
      <Page title="Usuários">
        <EmptyState title="Acesso restrito" description="Apenas administradores podem gerenciar usuários." />
      </Page>
    );
  }

  async function create() {
    if (!email.trim() || !displayName.trim() || password.length < 8 || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await api.createUser({ email: email.trim(), displayName: displayName.trim(), password, globalRole });
      setCreating(false);
      setEmail('');
      setDisplayName('');
      setPassword('');
      setGlobalRole('user');
      reload();
    } catch (e) {
      const code = (e as { error?: string }).error;
      setErr(code === 'email_taken' ? 'Já existe um usuário com esse e-mail.' : 'Falha ao criar usuário.');
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(u: AdminUser) {
    try {
      await api.updateUser(u.id, { active: !u.active });
      reload();
    } catch {
      setErr('Falha ao atualizar usuário.');
    }
  }

  async function setRole(u: AdminUser, role: GlobalRole) {
    try {
      await api.updateUser(u.id, { globalRole: role });
      reload();
    } catch {
      setErr('Falha ao atualizar papel.');
    }
  }

  async function resetPassword(u: AdminUser) {
    const pw = prompt(`Nova senha para ${u.email} (mín. 8 caracteres):`);
    if (!pw) return;
    if (pw.length < 8) {
      setErr('Senha muito curta.');
      return;
    }
    try {
      await api.updateUser(u.id, { password: pw });
      setErr(null);
      alert('Senha atualizada.');
    } catch {
      setErr('Falha ao redefinir senha.');
    }
  }

  return (
    <Page
      title="Usuários"
      subtitle="Crie e gerencie quem acessa o Neo-Pilots."
      actions={
        <Button variant="primary" onClick={() => setCreating(true)}>
          + Novo usuário
        </Button>
      }
    >
      {err && <div className="chat-error">{err}</div>}
      {loading ? (
        <div className="center">Carregando…</div>
      ) : (
        <Card>
          <table className="users-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>E-mail</th>
                <th>Papel</th>
                <th>Status</th>
                <th>Último login</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.displayName}</td>
                  <td>{u.email}</td>
                  <td>
                    <Select
                      value={u.globalRole}
                      onChange={(e) => setRole(u, e.target.value as GlobalRole)}
                      disabled={u.id === me?.id}
                    >
                      <option value="user">user</option>
                      <option value="admin">admin</option>
                    </Select>
                  </td>
                  <td>
                    {u.active ? <Badge variant="ok">ativo</Badge> : <Badge variant="neutral">inativo</Badge>}
                  </td>
                  <td className="users-muted">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : '—'}</td>
                  <td className="users-actions">
                    <Button size="sm" variant="ghost" onClick={() => resetPassword(u)}>
                      senha
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => toggleActive(u)}
                      disabled={u.id === me?.id}
                    >
                      {u.active ? 'desativar' : 'ativar'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Modal
        open={creating}
        onClose={() => setCreating(false)}
        title="Novo usuário"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreating(false)}>
              cancelar
            </Button>
            <Button
              variant="primary"
              onClick={create}
              disabled={busy || !email.trim() || !displayName.trim() || password.length < 8}
            >
              {busy ? 'criando…' : 'criar'}
            </Button>
          </>
        }
      >
        <Field label="Nome">
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} autoFocus />
        </Field>
        <Field label="E-mail">
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Senha" hint="Mínimo 8 caracteres.">
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <Field label="Papel">
          <Select value={globalRole} onChange={(e) => setGlobalRole(e.target.value as GlobalRole)}>
            <option value="user">user (acesso aos projetos atribuídos)</option>
            <option value="admin">admin (acesso total)</option>
          </Select>
        </Field>
      </Modal>
    </Page>
  );
}
